import { useState } from 'react';
import emailjs from '@emailjs/browser';
import './Contact.css';

/* ─────────────────────────────────────────────────────────────
   EmailJS setup (one-time, free, no backend needed)
   1. Create a free account at https://www.emailjs.com
   2. Add an Email Service connected to info@skillsyncpro.in
   3. Create an Email Template with variables:
      {{from_name}} {{from_email}} {{phone}} {{subject}} {{message}}
   4. Copy your Service ID, Template ID and Public Key below.
   Until these are filled in, the form falls back to opening the
   visitor's mail app addressed to info@skillsyncpro.in so nothing
   is ever lost.
   ───────────────────────────────────────────────────────────── */
const EMAILJS_SERVICE_ID  = 'YOUR_SERVICE_ID';
const EMAILJS_TEMPLATE_ID = 'YOUR_TEMPLATE_ID';
const EMAILJS_PUBLIC_KEY  = 'YOUR_PUBLIC_KEY';

const CONTACT_EMAIL = 'info@skillsyncpro.in';

const SUBJECTS = [
  'General Enquiry',
  'Web Development',
  'Mobile App Development',
  'UI/UX Design',
  'Cloud Solutions',
  'IT Consulting',
  'Career / Internship',
  'Other',
];

function IconPhone() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
      <path d="M4 2h3l1.5 4-2 1.5a11 11 0 005 5L13 10.5l4 1.5v3a2 2 0 01-2 2A16 16 0 012 4a2 2 0 012-2z"
        stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}
function IconLandline() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <rect x="4" y="3" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M4 14h16M8 18h1M12 18h1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M8 7h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
function IconMail() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
      <rect x="2" y="4" width="16" height="13" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M2 7l8 5 8-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
function IconGlobe() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.6" />
      <path d="M2 10h16M10 2c2.5 2.3 2.5 13.7 0 16M10 2c-2.5 2.3-2.5 13.7 0 16"
        stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}
function IconSend() {
  return (
    <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
      <path d="M18 2L9 11M18 2l-6 16-3-7-7-3 16-6z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}

const INFO_ROWS = [
  { label: 'Mobile',    value: '+91 81248 38545',      href: 'tel:+918124838545',           Icon: IconPhone },
  { label: 'Landline',  value: '04652 430729',          href: 'tel:04652430729',             Icon: IconLandline },
  { label: 'Email',     value: 'info@skillsyncpro.in',  href: 'mailto:info@skillsyncpro.in', Icon: IconMail },
  { label: 'Website',   value: 'www.skillsyncpro.in',   href: 'https://www.skillsyncpro.in', Icon: IconGlobe },
];

const initialForm = { name: '', email: '', phone: '', subject: SUBJECTS[0], message: '' };

export default function Contact() {
  const [form, setForm]     = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | sending | success | error

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = 'Please enter your name.';
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) er.email = 'Enter a valid email.';
    if (!form.message.trim()) er.message = 'Please add a short message.';
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    if (EMAILJS_SERVICE_ID.startsWith('YOUR_')) {
      // EmailJS not configured yet — fall back to opening the visitor's
      // mail app so the enquiry still reaches info@skillsyncpro.in.
      const body = `Name: ${form.name}%0AEmail: ${form.email}%0APhone: ${form.phone}%0ASubject: ${form.subject}%0A%0A${encodeURIComponent(form.message)}`;
      window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('Website Enquiry: ' + form.subject)}&body=${body}`;
      return;
    }

    setStatus('sending');
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: form.name,
          from_email: form.email,
          phone: form.phone,
          subject: form.subject,
          message: form.message,
          to_email: CONTACT_EMAIL,
        },
        { publicKey: EMAILJS_PUBLIC_KEY }
      );
      setStatus('success');
      setForm(initialForm);
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <div className="ctp">

      {/* ── Hero ── */}
      <section className="ctp-hero">
        <div className="ctp-hero__blob" aria-hidden="true" />
        <span className="ctp-hero__eyebrow">Contact Us</span>
        <h1 className="ctp-hero__title">Let&rsquo;s Build Something Great</h1>
        <div className="ctp-hero__rule" />
        <p className="ctp-hero__sub">
          Have a project in mind or a question about our services? Reach out and the
          SkillSync Pro team will get back to you shortly.
        </p>
      </section>

      {/* ── Body ── */}
      <section className="ctp-body">

        {/* Info card */}
        <div className="ctp-info">
          <h3 className="ctp-info__title">Get in Touch</h3>
          <p className="ctp-info__desc">
            Prefer to talk directly? Call, WhatsApp, or email us using the details below.
          </p>

          <div className="ctp-info__list">
            {INFO_ROWS.map(({ label, value, href, Icon }) => (
              <a key={label} href={href} className="ctp-info__row"
                 target={href.startsWith('http') ? '_blank' : undefined}
                 rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}>
                <span className="ctp-info__icon"><Icon /></span>
                <span>
                  <span className="ctp-info__row-label">{label}</span>
                  <span className="ctp-info__row-value">{value}</span>
                </span>
              </a>
            ))}
          </div>

          <a className="ctp-info__cta" href={`mailto:${CONTACT_EMAIL}`}>
            <IconMail /> Email Us Directly
          </a>
        </div>

        {/* Form card */}
        <div className="ctp-form-card">
          <h3 className="ctp-form-card__title">Send an Enquiry</h3>
          <p className="ctp-form-card__desc">
            Fill in the form below — it goes straight to <strong>{CONTACT_EMAIL}</strong>.
          </p>

          <form className="ctp-form" onSubmit={handleSubmit} noValidate>
            <div className={`ctp-field ${errors.name ? 'ctp-field--error' : ''}`}>
              <label htmlFor="name">Full Name<sup>*</sup></label>
              <input id="name" name="name" type="text" placeholder="Your name"
                     value={form.name} onChange={handleChange} />
              {errors.name && <span className="ctp-field__err">{errors.name}</span>}
            </div>

            <div className={`ctp-field ${errors.email ? 'ctp-field--error' : ''}`}>
              <label htmlFor="email">Email<sup>*</sup></label>
              <input id="email" name="email" type="email" placeholder="you@example.com"
                     value={form.email} onChange={handleChange} />
              {errors.email && <span className="ctp-field__err">{errors.email}</span>}
            </div>

            <div className="ctp-field">
              <label htmlFor="phone">Phone Number</label>
              <input id="phone" name="phone" type="tel" placeholder="+91 xxxxx xxxxx"
                     value={form.phone} onChange={handleChange} />
            </div>

            <div className="ctp-field">
              <label htmlFor="subject">Subject</label>
              <select id="subject" name="subject" value={form.subject} onChange={handleChange}>
                {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className={`ctp-field ctp-field--full ${errors.message ? 'ctp-field--error' : ''}`}>
              <label htmlFor="message">Message<sup>*</sup></label>
              <textarea id="message" name="message" placeholder="Tell us a bit about your project or query..."
                        value={form.message} onChange={handleChange} />
              {errors.message && <span className="ctp-field__err">{errors.message}</span>}
            </div>

            <div className="ctp-submit-row">
              <button type="submit" className="ctp-submit-btn" disabled={status === 'sending'}>
                {status === 'sending' ? <span className="ctp-spinner" /> : <IconSend />}
                {status === 'sending' ? 'Sending...' : 'Send Message'}
              </button>

              {status === 'success' && (
                <span className="ctp-submit-msg ctp-submit-msg--ok">
                  ✓ Thanks! Your message has been sent.
                </span>
              )}
              {status === 'error' && (
                <span className="ctp-submit-msg ctp-submit-msg--err">
                  Something went wrong. Please email us directly at {CONTACT_EMAIL}.
                </span>
              )}
            </div>
          </form>
        </div>

      </section>
    </div>
  );
}
